import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import {
  isEmpty,
  toString
} from 'lodash';
import moment from 'moment';
import {
  ELSURLHelper,
  ELSTokenHelper,
  ELSLoggingService,
  ELSCommonUIConstants,
  ELSTokenServiceRegistrar,
} from '@els/els-ui-common-react';
import { useHistory } from 'react-router';
import { ELSTokenUser } from '../../models/els.dtos';
import {
  removeCookies
} from '../app-link-redirect/app-link-redirect.utilities';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import { LoginQueryParam } from './login.constants';
import { RoutePath } from '../../components/app/app.constants';
import {
  fetchAllAppFeatureFlagsAction,
  fetchCourseSectionAction,
  fetchEvolveUsersAction,
  fetchIpAddressAction,
  putRefreshTokenAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import { AppContext } from '../../components/app/AppProvider.component';
import { storeTokens } from '../../utilities/token-manager.utilities';

export type LoginPageState = {
  hasError: boolean;
  errorMessage: string;
}

const defaultState: LoginPageState = {
  hasError: false,
  errorMessage: ''
};

const LoginPageFileName = 'Login.page.tsx';

const LoginPage = (): JSX.Element => {

  const [state, setState] = useState<LoginPageState>(defaultState);
  const history = useHistory();

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);

  const logError = (errorMessage: string) => {
    ELSLoggingService.error(LoginPageFileName, errorMessage);
    setState({
      hasError: true,
      errorMessage
    });
  };

  useEffect(() => {
    // Remove cookies if left around by other app
    removeCookies();

    const queryString: string = location.search;
    const token: string = ELSURLHelper.getParameterByName(LoginQueryParam.TOKEN, queryString);
    const refreshToken: string = ELSURLHelper.getParameterByName(LoginQueryParam.REFRESH_TOKEN, queryString);
    let ecrId: string = ELSURLHelper.getParameterByName(LoginQueryParam.ECR_ID, queryString);

    // Strip sensitive credentials from the URL so they don't persist in browser history,
    // server logs, or the Referer header on subsequent navigations.
    if (token || refreshToken) {
      const cleanParams = new URLSearchParams(queryString);
      cleanParams.delete(LoginQueryParam.TOKEN);
      cleanParams.delete(LoginQueryParam.REFRESH_TOKEN);
      const cleanSearch = cleanParams.toString();
      history.replace(`${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`);
    }

    setAppState((prevState) => {
      return { ...prevState, ecrId, };
    });

    if (token) {
      const tokenExpireDate = ELSTokenHelper.getExpirationDateFromToken(token);
      const now = moment();
      if (now.isAfter(tokenExpireDate)) {
        const errorMessage = 'Failed to login via LMS - Launching app by LTI launch with expired token';
        logError(errorMessage);
        return;
      }
      ELSTokenServiceRegistrar.register(token);
      setAppState((prevState) => {
        return {
          ...prevState,
          registeredToken: token,
        };
      });
    } else if (ELSTokenServiceRegistrar.isValidToken()) {
      // Token already registered (e.g., after refresh token rotation in AppRegistrar)
      setAppState((prevState) => {
        return {
          ...prevState,
          registeredToken: ELSTokenHelper.getToken(),
        };
      });
    } else {
      const errorMessage = 'Failed to login via LMS - Launching app by LTI launch with missing token';
      logError(errorMessage);
      return;
    }

    const tokenUser: ELSTokenUser = ELSTokenHelper.getUser();

    if (refreshToken) {
      putRefreshTokenAction({
        setAppState,
        appStateRef,
        appState,
        refreshToken
      })
        .then((response) => {
          storeTokens(response);
        })
        .catch(() => {
          ELSLoggingService.warn(LoginPageFileName, 'Failed to rotate refresh token on login; proceeding with existing JWT');
        });
    }

    if (isEmpty(tokenUser)) {
      const errorMessage = 'Failed to login via LMS - Launching app by LTI launch with invalid token';
      logError(errorMessage);
      return;
    }

    if (!tokenUser.appParams || !tokenUser.appParams.courseId) {
      const errorMessage = 'Failed to login via LMS - Launching app by LTI launch: missing courseSectionId';
      logError(errorMessage);
      return;
    }

    if (
      tokenUser.role !== ELSCommonUIConstants.userType.Student
      && tokenUser.role !== ELSCommonUIConstants.userType.Instructor
    ) {
      const errorMessage = 'User role is not supported';
      logError(errorMessage);
      return;
    }

    const userId = toString(tokenUser.userId);
    const courseSectionId = toString(tokenUser.appParams.courseId);
    if (!ecrId) {
      ecrId = tokenUser.appParams?.ecrId?.toString() || null;
    }

    setAppState((prevState) => {
      return {
        ...prevState,
        userId,
        userRole: tokenUser.role,
        courseSectionId,
        tokenIsbns: tokenUser.appParams.isbns,
        ecrId
      };
    });

    fetchEvolveUsersAction({
      setAppState,
      appStateRef,
      userId,
      useCache: false
    });

    fetchIpAddressAction({ appState, appStateRef, setAppState });

    Promise.all([
      fetchCourseSectionAction({
        setAppState,
        appStateRef,
        courseSectionId,
        useCache: false
      }),
      fetchAllAppFeatureFlagsAction({
        setAppState,
        appStateRef,
        useCache: false
      }),
    ]).then(() => {
      trackNewRelicAction({
        appStateRef,
        actionProps: { action: NewRelicAnalyticsAction.LOGIN_LAUNCH, props: null },
      });

      history.push(RoutePath.AI_CHAT);
    });
  }, []);

  const { hasError, errorMessage } = state;

  if (hasError) {
    return (
      <div className="u-els-padding-3x">
        <h2 className="c-els-heading c-els-heading--h4 u-els-color-primary">{errorMessage}</h2>
      </div>
    );
  }

  return (
    <div className="u-els-padding-3x">
      <h2 className="c-els-heading c-els-heading--h4 u-els-color-primary">Loading</h2>
    </div>
  );
};

export default LoginPage;
